import { memo, useState, useCallback, type ComponentPropsWithoutRef } from 'react'
import ReactMarkdown, { type ExtraProps } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import { useTranslation } from 'react-i18next'
import { IconCheck, IconCopy } from '../icons'
import { cn } from '../../lib/cn'
import { CITE_PREFIX } from './citations'

type HastNode = ExtraProps['node'] | { type: string; value?: string; children?: HastNode[] }

// rehype-highlight turns the code into token spans, so the raw text is read back from the hast node.
function hastText(node: HastNode | undefined): string {
  if (!node) return ''
  if ('value' in node && typeof node.value === 'string') return node.value
  return 'children' in node && Array.isArray(node.children) ? node.children.map((c) => hastText(c as HastNode)).join('') : ''
}

function CodeBlock({ children, className, node, ...props }: ComponentPropsWithoutRef<'code'> & ExtraProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const rawText = node ? hastText(node) : String(children ?? '')
  const codeText = rawText.replace(/\n$/, '')
  // Fenced blocks always end with a newline (or carry a language class); inline code never does.
  const isInline = !className && !rawText.includes('\n')

  const match = /language-([\w+#-]+)/.exec(className || '')
  const language = match ? match[1] : ''
  const lineCount = codeText.split('\n').length

  const handleCopy = useCallback(() => {
    void navigator.clipboard?.writeText(codeText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [codeText])

  if (isInline) {
    return <code className={className} {...props}>{children}</code>
  }

  return (
    <div className="code-block group/code my-3 overflow-hidden rounded-xl">
      <div className="code-block-header flex items-center justify-between px-3 py-1.5 text-xs font-mono select-none">
        <span className="flex items-center gap-2">
          <span className="flex gap-1.5" aria-hidden="true">
            <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
          </span>
          <span className="code-block-lang">{language || 'text'}</span>
        </span>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copied ? t('common.copied') : t('common.copy')}
          className="code-block-copy flex items-center gap-1 rounded px-1.5 py-0.5 text-xs transition-colors focus:outline-none"
        >
          {copied ? (
            <>
              <IconCheck className="h-3.5 w-3.5 text-[#98c379]" />
              <span className="text-[11px] text-[#98c379]">{t('common.copied')}</span>
            </>
          ) : (
            <>
              <IconCopy className="h-3.5 w-3.5" />
              <span className="text-[11px]">{t('common.copy')}</span>
            </>
          )}
        </button>
      </div>
      <div className="flex overflow-x-auto text-[13px] leading-relaxed">
        <div className="code-block-gutter shrink-0 select-none py-3 pl-3 pr-3 text-right font-mono" aria-hidden="true">
          {Array.from({ length: lineCount }, (_, i) => <div key={i}>{i + 1}</div>)}
        </div>
        <pre className="!m-0 min-w-0 flex-1 !rounded-none !border-0 !bg-transparent !py-3 !pl-3 !pr-4 !overflow-visible">
          <code className={className} {...props}>
            {children}
          </code>
        </pre>
      </div>
    </div>
  )
}

// react-markdown never renders raw HTML (no rehype-raw), so model output cannot inject markup.
/**
 * `className` replaces the feed's 15px body size: a short question in the prompt card wants the
 * card's own type scale, and the prose rules inherit size from this wrapper.
 */
export const Markdown = memo(function Markdown({ text, onCite, className = 'text-[15px]' }: { text: string; onCite?(n: number): void; className?: string }) {
  const { t } = useTranslation()
  return (
    <div className={cn('prose-chat', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeHighlight, { detect: true }]]}
        components={{
          a: ({ children, href }) => href?.startsWith(CITE_PREFIX) && onCite
            ? <button type="button" onClick={() => onCite(Number(href.slice(CITE_PREFIX.length)))} aria-label={t('kb.openCitation', { n: href.slice(CITE_PREFIX.length) })}
                className="mx-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-md bg-ginger-100 px-1 align-[1px] text-[11px] font-semibold text-ginger-600 hover:bg-ginger-300">
                {href.slice(CITE_PREFIX.length)}
              </button>
            : <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
          pre: ({ children }) => <>{children}</>,
          code: CodeBlock
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  )
})
